import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fecharComEsc } from '../../utils/impressaoVenda';
import { cabecalhoEmpresaHtml, carregarEmpresaRelatorio, escHtml } from '../../utils/cabecalhoRelatorio';

/**
 * Exportar/imprimir uma lista já carregada, com seleção de quais colunas
 * entram. "Excel" é gerado como CSV (abre direto no Excel, sem precisar da
 * lib xlsx — que tem vulnerabilidades conhecidas de prototype pollution/ReDoS
 * relevantes justamente pra LER arquivo malicioso; aqui só GRAVAMOS, mas
 * evitar a dependência de qualquer forma é mais simples e mais seguro).
 *
 * colunas: [{ key, label, padrao?: bool, render?: (linha) => string, direita?: bool }]
 * totais (opcional): { [key]: string } — vira uma linha TOTAL no fim, só nas
 * colunas marcadas que tiverem valor.
 * filtros (opcional): ['Período: 01/11/2026 a 04/11/2026', ...] — filtros com
 * que a lista foi gerada; saem no cabeçalho da impressão, do PDF e do CSV.
 * O cabeçalho da impressão e do PDF traz logo e dados da empresa do login.
 */
export default function ModalExportarLista({ titulo, colunas, dados, nomeArquivo, totais, filtros, onClose }) {
  const orgId = useAuth()?.usuario?.organizacao_id;
  // Se a empresa não carregar, a impressão sai só sem o cabeçalho dela.
  const empresaRef = useRef(null);
  const empresaProntaRef = useRef(!orgId);
  const empresaPromessaRef = useRef(Promise.resolve());
  useEffect(() => {
    if (!orgId) return;
    empresaPromessaRef.current = (async () => {
      empresaRef.current = await carregarEmpresaRelatorio(orgId);
      empresaProntaRef.current = true;
    })();
  }, [orgId]);
  const obterEmpresa = async () => {
    if (!empresaProntaRef.current) await empresaPromessaRef.current;
    return empresaRef.current;
  };
  const [selecionadas, setSelecionadas] = useState(
    Object.fromEntries(colunas.map(c => [c.key, c.padrao !== false]))
  );
  const toggle = k => setSelecionadas(s => ({ ...s, [k]: !s[k] }));
  const marcarTodas = (v) => setSelecionadas(Object.fromEntries(colunas.map(c => [c.key, v])));
  const colunasAtivas = colunas.filter(c => selecionadas[c.key]);
  const valor = (d, c) => {
    const v = c.render ? c.render(d) : d[c.key];
    return v == null ? '' : String(v);
  };
  const linhaTotal = totais
    ? colunasAtivas.map((c, i) => totais[c.key] != null ? String(totais[c.key]) : (i === 0 ? 'TOTAL' : ''))
    : null;

  const exportarCSV = () => {
    const preambulo = filtros?.length
      ? [...(empresaRef.current?.nome ? [empresaRef.current.nome] : []), titulo, ...filtros, ''].map(l => l.replace(/;/g, ','))
      : [];
    const linhas = [
      ...preambulo,
      colunasAtivas.map(c => c.label).join(';'),
      ...dados.map(d => colunasAtivas.map(c => valor(d, c).replace(/;/g, ',')).join(';')),
      ...(linhaTotal ? [linhaTotal.join(';')] : []),
    ];
    // BOM (﻿) — Excel só reconhece acentuação UTF-8 corretamente com ele.
    const blob = new Blob(['﻿' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nomeArquivo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarPDF = async () => {
    // jspdf só é carregado quando o usuário realmente pede um PDF —
    // evita engordar o bundle inicial de quem nunca usa essa opção.
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const doc = new jsPDF({ orientation: colunasAtivas.length > 4 ? 'landscape' : 'portrait' });
    const empresa = await obterEmpresa();
    let y = 12;
    if (empresa) {
      let xTexto = 14;
      if (empresa.logo) {
        try {
          const { width, height } = doc.getImageProperties(empresa.logo);
          const escala = Math.min(30 / width, 14 / height);
          doc.addImage(empresa.logo, 14, 8, width * escala, height * escala);
          xTexto = 48;
        } catch { /* formato de imagem que o PDF não aceita: segue sem logo */ }
      }
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(empresa.nome, xTexto, 12);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      const detalhes = [empresa.cnpj && `CNPJ: ${empresa.cnpj}`, empresa.endereco, empresa.cidadeUF].filter(Boolean);
      detalhes.slice(0, 2).forEach((l, i) => doc.text(l, xTexto, 16 + i * 4));
      if (detalhes[2]) doc.text(detalhes[2], xTexto, 24);
      y = 32;
    }
    doc.setFontSize(12);
    doc.text(titulo, 14, y);
    y += 5;
    doc.setFontSize(9);
    (filtros || []).forEach(l => { doc.text(l, 14, y); y += 4.5; });
    doc.text(`${dados.length} registro(s) — ${new Date().toLocaleString('pt-BR')}`, 14, y);
    autoTable(doc, {
      startY: y + 5,
      head: [colunasAtivas.map(c => c.label)],
      body: dados.map(d => colunasAtivas.map(c => valor(d, c))),
      ...(linhaTotal ? { foot: [linhaTotal], showFoot: 'lastPage' } : {}),
      columnStyles: Object.fromEntries(colunasAtivas.map((c, i) => [i, c.direita ? { halign: 'right' } : {}])),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [21, 101, 255] },
    });
    doc.save(`${nomeArquivo}.pdf`);
  };

  const imprimir = async () => {
    // Aberta antes do await: depois da chamada de rede o navegador bloqueia o pop-up.
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    const empresa = await obterEmpresa();
    const empresaHtml = cabecalhoEmpresaHtml(empresa);
    const filtrosHtml = (filtros || []).map(l => `<div class="fil">${escHtml(l)}</div>`).join('');
    const linhasHtml = dados.map(d =>
      `<tr>${colunasAtivas.map(c => `<td${c.direita ? ' class="r"' : ''}>${valor(d, c)}</td>`).join('')}</tr>`
    ).join('');
    const rodapeHtml = linhaTotal
      ? `<tfoot><tr>${linhaTotal.map((v, i) => `<td${colunasAtivas[i].direita ? ' class="r"' : ''}>${v}</td>`).join('')}</tr></tfoot>`
      : '';
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>${titulo}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;font-size:11px;padding:20px;}
  h2{font-size:15px;margin:0 0 4px;}
  p{font-size:11px;color:#666;margin:4px 0 12px;}
  .fil{font-size:11px;font-weight:bold;}
  table{width:100%;border-collapse:collapse;}
  th,td{border:1px solid #999;padding:4px 7px;text-align:left;}
  th{background:#eee;}
  .r{text-align:right;}
  tfoot td{font-weight:bold;background:#f3f3f3;}
  @media print{@page{margin:12mm;}}
</style></head><body>
  ${empresaHtml}
  <h2>${titulo}</h2>
  ${filtrosHtml}
  <p>${dados.length} registro(s) — ${new Date().toLocaleString('pt-BR')}</p>
  <table>
    <thead><tr>${colunasAtivas.map(c => `<th${c.direita ? ' class="r"' : ''}>${c.label}</th>`).join('')}</tr></thead>
    <tbody>${linhasHtml}</tbody>${rodapeHtml}
  </table>
</body></html>`;
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    const disparar = () => { win.focus(); win.print(); };
    // Espera a logo aparecer para ela não faltar na folha.
    const logo = win.document.querySelector?.('.emp img');
    if (logo && !logo.complete) { logo.onload = disparar; logo.onerror = disparar; } else disparar();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        width: 480, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ background: '#0a2540', color: '#fff', padding: '14px 20px', fontWeight: 700, fontSize: 14.5 }}>
          📤 Exportar / Imprimir — {titulo}
        </div>
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          <p style={{ fontSize: 12.5, color: '#666', margin: '0 0 10px' }}>
            {dados.length} registro(s) carregado(s). Escolha as colunas:
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <button type="button" onClick={() => marcarTodas(true)}
              style={{ fontSize: 11.5, background: 'none', border: 'none', color: '#1565FF', cursor: 'pointer', padding: 0 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => marcarTodas(false)}
              style={{ fontSize: 11.5, background: 'none', border: 'none', color: '#1565FF', cursor: 'pointer', padding: 0 }}>
              Desmarcar todas
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {colunas.map(c => (
              <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!selecionadas[c.key]} onChange={() => toggle(c.key)} />
                {c.label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex',
          justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={imprimir} disabled={!colunasAtivas.length || !dados.length}
            style={btnStyle()}>🖨️ Imprimir</button>
          <button onClick={exportarCSV} disabled={!colunasAtivas.length || !dados.length}
            style={btnStyle()}>📊 Excel (CSV)</button>
          <button onClick={exportarPDF} disabled={!colunasAtivas.length || !dados.length}
            style={btnStyle(true)}>📄 PDF</button>
          <button onClick={onClose} style={btnStyle()}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

const btnStyle = (primario) => ({
  padding: '7px 14px', borderRadius: 6, fontSize: 12.5, cursor: 'pointer',
  border: primario ? 'none' : '1px solid #d1d5db',
  background: primario ? '#1565FF' : '#fff',
  color: primario ? '#fff' : '#333',
  fontWeight: primario ? 700 : 500,
});
